import { toast } from "sonner";
import VOICE_LINES from "./voiceLines.json";

// Sons de notification (PLAN_SONS_NOTIFICATION.md) : « terminé » (tâche longue finie), « erreur » (échec),
// « message » (chat équipe, notification, annonce). Annonces vocales (Lot 3) : phrases rendues une fois par le
// moteur ElevenLabs du Voiceover (backend/scripts/generate_notification_voices.py → public/sounds/voix), chargées
// au premier geste ; bips synthétisés en Web Audio tant qu'une phrase n'est pas prête, voix coupée ou interface anglaise.
// Aucun effet à l'import (les tests jest mockent sonner) : tout passe par playSound / installSoundHooks.

// labelKey / hintKey : clés i18n, traduites au rendu (SoundSettingsButton)
export const SOUND_KINDS = [
  { id: "done", labelKey: "sounds.kinds.done.label", hintKey: "sounds.kinds.done.hint" },
  { id: "error", labelKey: "sounds.kinds.error.label", hintKey: "sounds.kinds.error.hint" },
  { id: "message", labelKey: "sounds.kinds.message.label", hintKey: "sounds.kinds.message.hint" },
];

const PREFS_KEY = "mw_sound_prefs";
const LAST_KEY = "mw_sound_last";   // horodatage par catégorie, PARTAGÉ entre onglets (anti-rafale)
const KEYS_KEY = "mw_sound_keys";   // événements déjà sonnés (job, prise, plan…), PARTAGÉS entre onglets
const KEY_TTL_MS = 15 * 60 * 1000;
const DEFAULT_PREFS = { enabled: true, voice: true, done: true, error: true, message: true };
const MIN_GAP_MS = { done: 3500, error: 2000, message: 1500 };   // rafales : 30 jobs finis = un son

// [fréquence Hz, départ s, durée s]
const TONES = {
  done: [[880, 0, 0.13], [1318.5, 0.12, 0.22]],
  error: [[392, 0, 0.16], [261.6, 0.15, 0.26]],
  message: [[1046.5, 0, 0.1], [1046.5, 0.14, 0.14]],
};
const WAVES = { done: "sine", error: "triangle", message: "sine" };
const PEAK_GAIN = 0.16;

// Annonces vocales : id de voiceLines.json → public/sounds/voix/<id>.mp3 (normalisé à -20 LUFS ≈ niveau des bips)
const VOICE_DIR = "/sounds/voix";
const DEFAULT_LINE = { done: "generation_terminee", error: "erreur_generique", message: "nouveau_message" };
const VOICE_GAIN = 1;
const QUEUE_GAP_S = 0.25;
const MAX_WAIT_S = { done: 4, message: 4, error: 8 };   // file d'attente : au-delà on abandonne (une erreur tient plus)
const FETCH_TIMEOUT_MS = 8000;

let memoryPrefs = null;   // repli si localStorage refuse l'écriture (navigation privée stricte) : couper reste effectif

export function getSoundPrefs() {
  if (memoryPrefs) return { ...memoryPrefs };
  try {
    return { ...DEFAULT_PREFS, ...(JSON.parse(localStorage.getItem(PREFS_KEY) || "{}") || {}) };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function setSoundPrefs(patch) {
  const prefs = { ...getSoundPrefs(), ...patch };
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    memoryPrefs = null;
  } catch {
    memoryPrefs = prefs;
  }
  try { window.dispatchEvent(new CustomEvent("mw:sound-prefs", { detail: prefs })); } catch { /* noop */ }
  if (lastGestureAt) preloadVoices();   // voix réactivée depuis le réglage
  return prefs;
}

let ctx = null;
let lastGestureAt = 0;
const localLast = {};

function audioContext() {
  if (ctx) return ctx;
  const AC = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
  if (!AC) return null;
  try { ctx = new AC(); } catch { ctx = null; }
  // Contexte suspendu (iOS « interrupted », veille) : on vide la file, sinon tout l'arriéré jouerait à la reprise
  try { ctx?.addEventListener?.("statechange", () => { if (ctx.state !== "running") stopScheduled(); }); } catch { /* noop */ }
  return ctx;
}

let busyUntil = 0;              // horloge audio : fin de la dernière annonce / du dernier bip programmé
const scheduled = new Set();    // sources programmées (voix et bips)
const voiceBuffers = new Map(); // id → AudioBuffer décodé (l'ArrayBuffer d'origine est inutilisable après décodage)
const voiceLoading = new Map(); // id → Promise<AudioBuffer|null>
const voiceBroken = new Set();  // fichier absent / illisible : bip pour cette phrase jusqu'au rechargement
let preloading = false;

function stopScheduled() {
  for (const src of scheduled) { try { src.stop(); } catch { /* déjà fini */ } }
  scheduled.clear();
  busyUntil = 0;
}

function track(src) {
  scheduled.add(src);
  src.onended = () => scheduled.delete(src);
}

/** Départ dans la file (horloge audio, pas de setTimeout : les onglets cachés ralentissent les timers) ; null = trop
 *  d'attente, abandonné. Une annonce ne chevauche jamais la précédente. */
function queueStart(c, kind, force) {
  const when = Math.max(c.currentTime + 0.01, busyUntil);
  return !force && when - c.currentTime > MAX_WAIT_S[kind] ? null : when;
}

function uiIsFrench() {
  // Langue choisie par i18n (détecteur → localStorage) ; pas <html lang>, qui vaut « en » avant son initialisation
  try {
    return !/^en/i.test(localStorage.getItem("i18nextLng") || "fr");
  } catch {
    return true;
  }
}

function hasLine(id) {
  return !!id && Object.prototype.hasOwnProperty.call(VOICE_LINES, id);
}

function voiceOn(prefs) {
  return prefs.voice !== false && uiIsFrench();
}

function decode(c, data) {
  // Safari ancien : decodeAudioData à rappels seulement ; les autres : promesse
  return new Promise((resolve, reject) => {
    try {
      const p = c.decodeAudioData(data, resolve, reject);
      if (p && typeof p.then === "function") p.then(resolve, reject);
    } catch (e) { reject(e); }
  });
}

/** Charge et décode une phrase (une fois). Promesse qui ne rejette jamais : AudioBuffer, ou null. */
function loadVoice(id) {
  const c = audioContext();
  if (!c || !hasLine(id) || voiceBroken.has(id)) return Promise.resolve(null);
  if (voiceBuffers.has(id)) return Promise.resolve(voiceBuffers.get(id));
  if (typeof fetch !== "function" || typeof c.decodeAudioData !== "function") return Promise.resolve(null);
  if (voiceLoading.has(id)) return voiceLoading.get(id);
  // Requête bloquée : abandonnée au bout de 8 s (sinon le préchargement et « Écouter » resteraient suspendus)
  const abort = typeof AbortController === "function" ? new AbortController() : null;
  const timer = abort ? setTimeout(() => abort.abort(), FETCH_TIMEOUT_MS) : null;
  const p = fetch(`${VOICE_DIR}/${id}.mp3`, abort ? { signal: abort.signal } : undefined)
    .then((res) => {
      // Fichier absent : Firebase répond 200 + index.html (réécriture SPA), le dev 404 → définitif pour cette phrase.
      // Autre statut d'erreur (5xx, 429) : passager, retenté au geste suivant
      const type = res.headers?.get?.("content-type") || "";
      if (res.status === 404 || (res.ok && !/^audio\//i.test(type))) { voiceBroken.add(id); return null; }
      if (!res.ok) return null;
      return res.arrayBuffer().then((data) => decode(c, data).then(
        (buf) => { voiceBuffers.set(id, buf); return buf; },
        () => { voiceBroken.add(id); return null; },   // pas un mp3 décodable : définitif
      ));
    }, () => null)   // réseau (ici ou pendant la lecture du corps, via le catch) : retenté au préchargement suivant
    .catch(() => null)
    .finally(() => { clearTimeout(timer); voiceLoading.delete(id); });
  voiceLoading.set(id, p);
  return p;
}

/** Précharge toutes les phrases, une par une, au premier geste (puis à chaque geste tant qu'il en manque). */
function preloadVoices() {
  if (preloading) return;
  const prefs = getSoundPrefs();
  if (!prefs.enabled || !voiceOn(prefs)) return;
  const ids = Object.keys(VOICE_LINES).filter((id) => !voiceBuffers.has(id) && !voiceBroken.has(id));
  if (!ids.length) return;
  preloading = true;
  ids.reduce((chain, id) => chain.then(() => loadVoice(id)), Promise.resolve())
    .catch(() => {})
    .finally(() => { preloading = false; });
}

function playVoice(c, buf, when) {
  const src = c.createBufferSource();
  const gain = c.createGain();
  src.buffer = buf;
  gain.gain.value = VOICE_GAIN;
  src.connect(gain);
  gain.connect(c.destination);
  track(src);
  src.start(when);
  busyUntil = when + buf.duration + QUEUE_GAP_S;
}

function claimSlot(kind) {
  // Un seul onglet joue un même événement : poll repris dans deux onglets, message SSE reçu partout
  const now = Date.now();
  let last = localLast[kind] || 0;
  try { last = Math.max(last, (JSON.parse(localStorage.getItem(LAST_KEY) || "{}") || {})[kind] || 0); } catch { /* noop */ }
  if (now - last < MIN_GAP_MS[kind]) return false;
  localLast[kind] = now;
  try {
    const all = JSON.parse(localStorage.getItem(LAST_KEY) || "{}") || {};
    localStorage.setItem(LAST_KEY, JSON.stringify({ ...all, [kind]: now }));
  } catch { /* noop */ }
  return true;
}

const localKeys = new Map();

function claimKey(key) {
  // Deux onglets pollent le même job / la même prise à des moments décalés (bien au-delà de l'anti-rafale) :
  // l'événement lui-même n'est sonné qu'une fois, dans le premier onglet qui le voit
  const now = Date.now();
  if (localKeys.has(key) && now - localKeys.get(key) < KEY_TTL_MS) return false;
  if (localKeys.size > 300) {
    for (const [k, ts] of localKeys) if (now - ts > KEY_TTL_MS) localKeys.delete(k);
  }
  let keys = {};
  try { keys = JSON.parse(localStorage.getItem(KEYS_KEY) || "{}") || {}; } catch { /* noop */ }
  if (keys[key] && now - keys[key] < KEY_TTL_MS) return false;
  localKeys.set(key, now);
  try {
    const fresh = Object.fromEntries(Object.entries(keys).filter(([, ts]) => now - ts < KEY_TTL_MS).slice(-200));
    localStorage.setItem(KEYS_KEY, JSON.stringify({ ...fresh, [key]: now }));
  } catch { /* noop */ }
  return true;
}

// Événements déjà MONTRÉS par une page de CET onglet (toast, carte de prise…) : le veilleur global des fins de tâche
// (TaskEndWatcher) n'y ajoute pas de toast. Noté même son coupé ou avant le premier geste. Le son, lui, est dédoublonné
// par claimKey : une page qui montre sans sonner (SSE video_ready, sans user_id) laisse le veilleur sonner.
const handledKeys = new Map();

export function markHandled(key) {
  const now = Date.now();
  handledKeys.set(key, now);
  if (handledKeys.size > 300) {
    for (const [k, ts] of handledKeys) if (now - ts > KEY_TTL_MS) handledKeys.delete(k);
  }
}

export function wasHandled(key) {
  const ts = handledKeys.get(key);
  return !!ts && Date.now() - ts < KEY_TTL_MS;
}

function scheduleTones(c, kind, when) {
  const t0 = when ?? c.currentTime + 0.01;
  busyUntil = t0 + Math.max(...TONES[kind].map(([, start, dur]) => start + dur)) + QUEUE_GAP_S;
  for (const [freq, start, dur] of TONES[kind]) {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = WAVES[kind];
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, t0 + start);
    gain.gain.exponentialRampToValueAtTime(PEAK_GAIN, t0 + start + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + start + dur);
    osc.connect(gain);
    gain.connect(c.destination);
    track(osc);
    osc.start(t0 + start);
    osc.stop(t0 + start + dur + 0.03);
  }
}

function mediaPlaying() {
  try {
    return Array.from(document.querySelectorAll("video, audio")).some((m) => !m.paused && !m.muted && m.volume > 0);
  } catch {
    return false;
  }
}

/** Voix si la phrase est déjà décodée, sinon bip (et la phrase se charge pour la fois suivante) : la clé et le créneau
 *  sont déjà pris, on ne les fait jamais attendre un chargement. « Écouter » (force), sans clé, peut attendre la voix. */
function announce(c, kind, say, force) {
  const line = hasLine(say) ? say : DEFAULT_LINE[kind];
  const voice = voiceOn(getSoundPrefs());
  const buf = voice ? voiceBuffers.get(line) : null;
  if (!buf && voice && force && !voiceBroken.has(line) && typeof fetch === "function" && typeof c.decodeAudioData === "function") {
    loadVoice(line).then((loaded) => {
      if (c.state !== "running") return;
      const when = queueStart(c, kind, true);
      if (loaded) playVoice(c, loaded, when); else scheduleTones(c, kind, when);
    }).catch(() => {});
    return;
  }
  const when = queueStart(c, kind, force);
  if (when === null) return;   // file trop longue (rafale de catégories différentes) : abandonné
  if (buf) {
    playVoice(c, buf, when);
    return;
  }
  scheduleTones(c, kind, when);
  if (voice) loadVoice(line);
}

/**
 * Joue le son d'une catégorie ("done" | "error" | "message"), si l'utilisateur ne l'a pas coupé.
 * `key` : identifiant de l'événement (job, prise, plan…) — sonné une seule fois, tous onglets confondus.
 * `force` : « Écouter » du réglage (ignore préférences, anti-rafale et clé).
 * `say` : annonce vocale (id de voiceLines.json) ; absente ou inconnue → la phrase par défaut de la catégorie.
 */
export function playSound(kind, { force = false, key = null, say = null } = {}) {
  try {
    if (key) markHandled(key);
    if (!TONES[kind]) return;
    if (!force) {
      const prefs = getSoundPrefs();
      if (!prefs.enabled || !prefs[kind]) return;
      // Jamais avant le premier geste dans la page : aucun son au chargement, même si le navigateur autorise l'audio
      if (!lastGestureAt) return;
      if (kind !== "error" && mediaPlaying()) return;   // relecture d'une prise avec dialogue
    }
    const c = audioContext();
    if (!c) return;
    if (c.state !== "running") {
      // Suspendu (iOS « interrupted »…) : on ne programme rien — un contexte suspendu fige le temps et jouerait
      // tout l'arriéré d'un coup. « Écouter » est un geste : on reprend puis on joue.
      const resumed = c.resume?.();
      if (force && resumed?.then) resumed.then(() => { if (c.state === "running") announce(c, kind, say, true); }).catch(() => {});
      else resumed?.catch?.(() => {});
      return;
    }
    if (!force && key && !claimKey(key)) return;
    if (!force && !claimSlot(kind)) return;
    announce(c, kind, say, force);
  } catch { /* un son ne doit jamais casser l'app */ }
}

/** Fin d'une tâche LONGUE : son « terminé » + toast de succès (mêmes options, ex. { id } d'un toast de chargement). */
export function toastDone(message, opts, soundOpts) {
  playSound("done", soundOpts);
  return opts === undefined ? toast.success(message) : toast.success(message, opts);
}

/** Échec d'une tâche LONGUE connue (job, prise…) : son « erreur » à clé (une fois tous onglets confondus) + toast
 *  d'erreur, sans repasser par le son automatique de toast.error (sinon l'autre onglet resonnerait). */
export function toastFailed(message, opts, soundOpts) {
  playSound("error", { say: "erreur_generation", ...soundOpts });
  const showError = toast.error && toast.error[PATCHED] ? toast.error[PATCHED] : toast.error;
  return opts === undefined ? showError(message) : showError(message, opts);
}

const recentErrors = new Map();   // message → horodatage : un poll qui échoue en boucle ne sonne qu'une fois / 5 min

function shouldRingError(args) {
  const now = Date.now();
  // Réponse immédiate à un clic (validation « choisis une image ») : visible, pas besoin de son
  if (now - lastGestureAt < 2500) return false;
  const key = typeof args[0] === "string" ? args[0] : "";
  if (key) {
    const prev = recentErrors.get(key);
    if (prev && now - prev < 5 * 60 * 1000) return false;
    recentErrors.set(key, now);
    if (recentErrors.size > 50) recentErrors.delete(recentErrors.keys().next().value);
  }
  return true;
}

const PATCHED = Symbol.for("mw.soundPatched");
let hooksInstalled = false;

/** À appeler une fois au démarrage (App.js). Idempotent : StrictMode et Fast Refresh peuvent le rappeler. */
export function installSoundHooks() {
  if (typeof window === "undefined") return;
  // Erreurs : enveloppe toast.error une seule fois (on repart toujours de l'original conservé)
  const current = toast.error;
  const original = current && current[PATCHED] ? current[PATCHED] : current;
  if (typeof original === "function") {
    const wrapped = (...args) => {
      try { if (shouldRingError(args)) playSound("error"); } catch { /* noop */ }
      return original(...args);
    };
    wrapped[PATCHED] = original;
    toast.error = wrapped;
  }
  if (hooksInstalled) return;
  hooksInstalled = true;
  const unlock = (e) => {
    try {
      // Taper un prompt n'est pas un « geste » (un vrai échec arrivé pendant la frappe doit sonner) ; Entrée /
      // Espace valident. Dépôt de fichier, sélecteur natif, collage : la validation qui suit est instantanée.
      if (e?.type === "keydown" && e.key !== "Enter" && e.key !== " ") return;
      lastGestureAt = Date.now();
      const c = audioContext();
      const resumed = c && c.state !== "running" ? c.resume?.() : null;
      resumed?.catch?.(() => {});
      preloadVoices();   // les annonces sont prêtes avant la première fin de tâche
    } catch { /* noop */ }
  };
  for (const ev of ["pointerdown", "touchend", "keydown", "drop", "dragend", "change", "paste"]) {
    window.addEventListener(ev, unlock, { capture: true, passive: true });
  }
  // Plan de l'agent exécuté (useAgent.js émet l'événement, personne d'autre ne l'écoutait)
  window.addEventListener("agent:plan-finished", (e) => {
    const status = e?.detail?.status;
    const key = e?.detail?.planId ? `plan:${e.detail.planId}` : null;
    const kind = status === "done" ? "done" : status === "failed" ? "error" : null;
    if (!kind) return;
    playSound(kind, { key, say: kind === "done" ? "agent_termine" : "erreur_agent" });
    // Les générations des étapes sont des tâches Toolbox de l'utilisateur : on prend leurs clés (muettes, l'anti-rafale
    // vient de jouer) pour que le veilleur global (TaskEndWatcher) ne les re-sonne pas après le plan
    for (const id of Array.isArray(e?.detail?.assetIds) ? e.detail.assetIds : []) {
      playSound(kind, { key: `job:${id}` });
      if (kind === "error") playSound("error", { key: `job:${id}:failed` });
    }
  });
}
